import React, { useCallback, useState, useEffect, useMemo } from 'react'
import { useNavigate } from 'react-router-dom'
import { useForm } from 'react-hook-form'
import DataSource from 'devextreme/data/data_source'
import Form, { ButtonItem, SimpleItem, EmptyItem, ColCountByScreen, Label, GroupItem } from 'devextreme-react/form'
import 'devextreme-react/tag-box'
import JumboCardQuick from '@jumbo/components/JumboCardQuick'
import Typography from '@mui/material/Typography'
import { PatternRule } from 'devextreme-react/data-grid'
import GenderTypeService from 'app/pages/registrations/customers/components/GenderTypeService'
import RelationshipTypeService from './RelationshipTypeService'
import customerServices from 'app/services/pages/reports/registrations/CustomerServices/customerServices'
import utilServices from 'app/services/util-services'
import { useSelector } from 'react-redux'
import CustomStore from 'devextreme/data/custom_store'
import { useSnackbar } from 'notistack'
import familyAndMemberService from 'app/services/pages/reports/registrations/familyAndMemberService/familyAndMemberService'
import { IconButton, Stack, useMediaQuery } from '@mui/material'
import WestIcon from '@mui/icons-material/West'

const AddFamilyMemberForm = (props) => {
    const navigate = useNavigate()
    const { enqueueSnackbar } = useSnackbar()
    const { handleSubmit } = useForm()
    const [showPrimaryContact, setShowPrimaryContact] = useState(false)
    const { loading } = useSelector((state) => state.members)
    const [labelLocation, setLabelLocation] = useState('left')
    const sm = useMediaQuery('(max-width:600px)')

    useEffect(() => {
        setLabelLocation(sm ? 'top' : 'left')
    }, [sm])

    function handleError(error) {
        if (error.status === '401') {
            navigate('/profile/signout')
        } else if (error.detail) {
            enqueueSnackbar(error.detail, {
                variant: 'error',
                anchorOrigin: {
                    vertical: 'top',
                    horizontal: 'right',
                },
            })
        } else {
            enqueueSnackbar(error, {
                variant: 'error',
                anchorOrigin: {
                    vertical: 'top',
                    horizontal: 'right',
                },
            })
        }
    }

    const validationRules = {
        familyName: [{ type: 'required', message: 'Family name is required.' }],
        firstName: [{ type: 'required', message: 'First name is required.' }],
        email: [{ type: 'email', message: 'Email is invalid.', ignoreEmptyValue: true }],
        genderType: [{ type: 'required', message: 'Gender is required.' }],
        relationshipType: [{ type: 'required', message: 'Relationship is required.' }],
    }

    const familyLookupList = useCallback(async (searchTerm) => {
        try {
            return await familyAndMemberService.getFamilyLookupList({ familyName: searchTerm })
        } catch (error) {
            handleError(error)
        }
    }, [])

    const familyLookupById = useCallback(async (key) => {
        try {
            return await familyAndMemberService.getFamilyLookupById(key)
        } catch (error) {
            handleError(error)
        }
    }, [])

    const familyLookupsStore = useMemo(() => {
        return new DataSource({
            store: new CustomStore({
                key: 'id',
                load: async function (loadOptions) {
                    let searchTerm = ''
                    if (loadOptions.searchValue) {
                        searchTerm = loadOptions.searchValue
                    }
                    return await familyLookupList(searchTerm)
                },
                byKey: async function (key) {
                    if (key === '') return utilServices.emptyList()

                    return familyLookupById(key)
                },
                errorHandler: function (e) {
                    console.log(e)
                },
            }),
            reshapeOnPush: true,
        })
    }, [])

    const familyEditorOptions = useMemo(
        () => ({
            dataSource: familyLookupsStore,
            valueExpr: 'id',
            searchEnabled: true,
            showClearButton: true,
            itemTemplate: function (data) {
                return data ? data.familyName.toFamilyContactName(data.contactName) : ''
            },
            displayExpr: function (data) {
                return data ? data.familyName.toFamilyContactName(data.contactName) : ''
            },
        }),
        [familyLookupsStore]
    )

    const genderEditorOptions = {
        dataSource: GenderTypeService.getGenderType(),
        displayExpr: 'genderType',
        valueExpr: 'id',
        showClearButton: true,
    }

    const relationshipEditorOptions = {
        dataSource: RelationshipTypeService.getRelationshipType(),
        displayExpr: 'relationshipType',
        valueExpr: 'id',
        showClearButton: true,
    }

    const submitButtonOptions = {
        text: 'Save',
        type: 'default',
        width: 120,
        useSubmitBehavior: true,
    }

    const cancelButtonOptions = {
        text: 'Cancel',
        type: 'Normal',
        width: 120,
        onClick: () =>
            navigate('/registrations/families', {
                state: { navigateFrom: 'cancel-add-member' },
            }),
    }

    const phoneEditorOptions = {
        valueChangeEvent: 'keyup',
        maskRules: {
            X: /\d/,
        },
    }

    const phonePattern = /^\+[1-9]\d{10,14}$/

    const primaryContactLookupList = useCallback(async (searchTerm) => {
        try {
            return await customerServices.getCustomerLookupList({ firstName: searchTerm })
        } catch (error) {
            handleError(error)
        }
    }, [])

    const primaryContactLookupById = useCallback(async (key) => {
        try {
            return await customerServices.getCustomerLookupById(key)
        } catch (error) {
            handleError(error)
        }
    }, [])

    const primaryContactLookupsStore = useMemo(() => {
        return new DataSource({
            store: new CustomStore({
                key: 'id',
                load: async function (loadOptions) {
                    let searchTerm = ''
                    if (loadOptions.searchValue) {
                        searchTerm = loadOptions.searchValue
                    }
                    return await primaryContactLookupList(searchTerm)
                },
                byKey: async function (key) {
                    if (key === '') return utilServices.emptyList()

                    return primaryContactLookupById(key)
                },
                errorHandler: function (e) {
                    console.log(e)
                },
            }),
            reshapeOnPush: true,
        })
    }, [])

    const primaryContactEditorOptions = {
        dataSource: primaryContactLookupsStore,
        valueExpr: 'id',
        searchEnabled: true,
        showClearButton: true,
        itemTemplate: function (data) {
            return data ? data.firstName.toFullName(data.lastName) : ''
        },
        displayExpr: function (data) {
            return data ? data.firstName.toFullName(data.lastName) : ''
        },
    }

    const onSelectionChanged = (e) => {
        // Update the state based on the value of e.value
        setShowPrimaryContact(e.value)
    }

    const checkBoxEditorOption = {
        onValueChanged: onSelectionChanged,
    }

    return (
        <React.Fragment>
            <JumboCardQuick
                title={
                    <Stack direction='row' alignItems='center' spacing={1}>
                        <IconButton
                            className='backArrowForm'
                            onClick={() =>
                                navigate(`/registrations/families`, {
                                    state: { navigateFrom: 'cancel-add-member' },
                                })
                            }>
                            <WestIcon />
                        </IconButton>
                        <Typography color={'common.white'} variant={'h3'} sx={{ mt: 0.8 }}>
                            Add Member
                        </Typography>
                    </Stack>
                }
                headerSx={{
                    height: 40,
                    bgcolor: 'primary.main',
                }}>
                <form onSubmit={handleSubmit(props.addMember)}>
                    <Form
                        mode='form'
                        formData={props.memberData}
                        showColonAfterLabel={false}
                        showValidationSummary={false}
                        labelLocation={labelLocation}>
                        <GroupItem caption='New Member' cssClass='form-group-item'>
                            <ColCountByScreen lg={13} md={13} sm={1} xs={1} />
                            <GroupItem />
                            <GroupItem />
                            <GroupItem />
                            <GroupItem cssClass='form-group-item' colSpan={6}>
                                <EmptyItem />
                                <SimpleItem
                                    dataField='isPrimary'
                                    editorType='dxCheckBox'
                                    editorOptions={checkBoxEditorOption}
                                />
                                {showPrimaryContact && (
                                    <SimpleItem
                                        editorType='dxSelectBox'
                                        dataField='customerId'
                                        editorOptions={primaryContactEditorOptions}>
                                        <Label text='Primary Contact' />
                                    </SimpleItem>
                                )}
                                <SimpleItem
                                    dataField='familyId'
                                    editorType='dxSelectBox'
                                    editorOptions={familyEditorOptions}>
                                    <Label text='Family' />
                                </SimpleItem>
                                <SimpleItem dataField='firstName' validationRules={validationRules.firstName} />
                                <SimpleItem dataField='lastName' />
                                <SimpleItem dataField='email' validationRules={validationRules.email}>
                                    <Label text='Email (Optional)' />
                                </SimpleItem>
                                <SimpleItem dataField='phoneNumber' editorOptions={phoneEditorOptions}>
                                    <Label text='Phone Number (Optional)' />
                                    <PatternRule
                                        message='phone number must start with + and needs to include country code'
                                        pattern={phonePattern}
                                    />
                                </SimpleItem>
                                <SimpleItem
                                    editorType='dxSelectBox'
                                    dataField='genderType'
                                    validationRules={validationRules.genderType}
                                    editorOptions={genderEditorOptions}
                                />
                                <SimpleItem
                                    editorType='dxSelectBox'
                                    dataField='relationshipType'
                                    validationRules={validationRules.relationshipType}
                                    editorOptions={relationshipEditorOptions}>
                                    <Label text='Relationship' />
                                </SimpleItem>
                                <SimpleItem
                                    dataField='birthDate'
                                    editorType='dxDateBox'
                                    editorOptions={{
                                        displayFormat: 'shortDate',
                                        type: 'date',
                                        pickerType: 'calendar',
                                        useMaskBehavior: true,
                                    }}
                                />
                            </GroupItem>
                        </GroupItem>
                        <EmptyItem />
                        <GroupItem>
                            <ColCountByScreen lg={2} md={2} sm={2} xs={2} />
                            <ButtonItem
                                buttonOptions={{
                                    disabled: loading,
                                    icon: loading ? 'refresh' : 'check',
                                    ...submitButtonOptions,
                                }}
                                horizontalAlignment='right'
                            />
                            <ButtonItem buttonOptions={cancelButtonOptions} horizontalAlignment='left' />
                        </GroupItem>
                    </Form>
                </form>
            </JumboCardQuick>
        </React.Fragment>
    )
}

export default AddFamilyMemberForm
